'use client';

import { FormEvent, useState } from 'react';
import { useRouter } from 'next/navigation';

type AuthUser = {
  id: number;
  email: string | null;
  telegram_id?: number | null;
  username?: string | null;
  display_name?: string | null;
  name: string;
  company?: string | null;
  role: string;
  timezone: string;
  auth_provider: string;
  created_at?: string;
};

export default function AuthPage() {
  const router = useRouter();

  const [tab, setTab] = useState<'login' | 'register'>('login');

  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [registerCompany, setRegisterCompany] = useState('');
  const [hasTelegramRegistration, setHasTelegramRegistration] = useState(false);
  const [registerTelegramId, setRegisterTelegramId] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  function getRedirectPath(user: AuthUser) {
    const params = new URLSearchParams();
    params.set('uid', String(user.id));
    params.set('auth', '1');

    if (user.telegram_id) {
      params.set('tgid', String(user.telegram_id));
    }

    if (user.role === 'teacher') return `/account/teacher?${params.toString()}`;
    if (user.role === 'admin') return `/account/admin?${params.toString()}`;
    return `/account/student?${params.toString()}`;
  }

//    if (user.role === 'teacher') return `/account/teacher?uid=${user.id}`;
//    if (user.role === 'admin') return `/account/admin?uid=${user.id}`;
//    return `/account/student?uid=${user.id}`;
//  }

//  const [user, setUser] = useState<AuthUser | null>(null);

  async function handleRegister(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');
//    setUser(null);

    if (!registerName.trim()) {
      setError('Full name is required');
      return;
    }

    if (!registerEmail.trim()) {
      setError('Email is required');
      return;
    }

    if (registerPassword !== registerConfirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (hasTelegramRegistration && !registerTelegramId.trim()) {
      setError('Telegram ID is required');
      return;
    }

    setLoading(true);

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: registerName,
          email: registerEmail,
          password: registerPassword,
          company: registerCompany || null,
          hasTelegramRegistration,
          telegramId: hasTelegramRegistration ? registerTelegramId.trim() : null,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Registration failed');
        return;
      }

      setSuccess('Account created successfully');
//      setUser(data.user);

      setRegisterName('');
      setRegisterEmail('');
      setRegisterPassword('');
      setRegisterConfirmPassword('');
      setRegisterCompany('');
      setRegisterTelegramId('');
      setHasTelegramRegistration(false);

      router.push(getRedirectPath(data.user));
      return;
    } catch (err) {
      console.error(err);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSuccess('');
//    setUser(null);

    setLoading(true);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: loginEmail,
          password: loginPassword,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Login failed');
        return;
      }

      setSuccess('Signed in successfully');
//      setUser(data.user);
      setLoginPassword('');

      router.push(getRedirectPath(data.user));
      return;
    } catch (err) {
      console.error(err);
      setError('Network error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-[calc(100vh-110px)] bg-gray-50 px-4 py-10">
      <div className="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <h1 className="mb-2 text-center text-2xl font-bold text-gray-900">
          Welcome to Classroom
        </h1>
        <p className="mb-6 text-center text-sm text-gray-500">
          Sign in to your account or create a new one
        </p>

        <div className="mb-6 grid grid-cols-2 rounded-xl bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => {
              setTab('login');
              setError('');
              setSuccess('');
              {/* setUser(null); */}
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
              tab === 'login'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Sign in
          </button>

          <button
            type="button"
            onClick={() => {
              setTab('register');
              setError('');
              setSuccess('');
              {/* setUser(null); */}
            }}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition ${
              tab === 'register'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Register
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="mb-4 rounded-xl border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">
            {success}
          </div>
        )}

        {tab === 'login' ? (
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-1 text-sm outline-none focus:border-gray-400"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-1 text-sm outline-none focus:border-gray-400"
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-blue-600 px-4 py-2 font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </button>

            <div className="pt-2">
              {/* <button
                type="button"
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-1 font-medium text-gray-700 transition hover:bg-gray-50"
              >
                Continue with Telegram
              </button> */}
              <a
                href="/auth-telegram/auth"
                className="block w-full rounded-xl border border-gray-300 bg-white px-4 py-1 text-center font-medium text-gray-700 transition hover:bg-gray-50"
              >
                Continue with Telegram
              </a>
            </div>
          </form>
        ) : (
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Full name
              </label>
              <input
                type="text"
                value={registerName}
                onChange={(e) => setRegisterName(e.target.value)}
                placeholder="Your full name"
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-1 text-sm outline-none focus:border-gray-400"
                autoComplete="name"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                value={registerEmail}
                onChange={(e) => setRegisterEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-1 text-sm outline-none focus:border-gray-400"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Password
              </label>
              <input
                type="password"
                value={registerPassword}
                onChange={(e) => setRegisterPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-1 text-sm outline-none focus:border-gray-400"
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Confirm password
              </label>
              <input
                type="password"
                value={registerConfirmPassword}
                onChange={(e) => setRegisterConfirmPassword(e.target.value)}
                placeholder="Repeat your password"
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-1 text-sm outline-none focus:border-gray-400"
                autoComplete="new-password"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Company or promo code <span className="text-gray-400">(optional)</span>
              </label>
              <input
                type="text"
                value={registerCompany}
                onChange={(e) => setRegisterCompany(e.target.value)}
                placeholder="Company name"
                className="w-full rounded-xl border border-gray-300 bg-white px-4 py-1 text-sm outline-none focus:border-gray-400"
                autoComplete="organization"
              />
            </div>

            <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
              <label className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    Do you already have Telegram registration?
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    Turn this on ONLY if you were already registered through Telegram.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setHasTelegramRegistration((prev) => !prev)}
                  className={`relative inline-flex h-5 w-11 items-center rounded-full transition ${
                    hasTelegramRegistration ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-6 transform rounded-full bg-white transition ${
                      hasTelegramRegistration ? 'translate-x-3' : 'translate-x-1'
                    }`}
                  />
                </button>
              </label>
            </div>

            {hasTelegramRegistration && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Telegram ID
                </label>
                <input
                  type="text"
                  value={registerTelegramId}
                  onChange={(e) => setRegisterTelegramId(e.target.value)}
                  placeholder="Enter your Telegram ID"
                  className="w-full rounded-xl border border-gray-300 bg-blue-200 px-4 py-1 text-sm outline-none focus:border-gray-400"
                  inputMode="numeric"
                />
                <p className="mt-1 text-xs text-gray-500">
                  We will link your website login to your existing Telegram account.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-xl bg-blue-600 px-4 py-2 font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </button>
          </form>
        )}

        {/* {user && (
          <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="mb-2 text-sm font-semibold text-gray-900">
              Response preview
            </div>
            <div className="space-y-1 text-sm text-gray-700">
              <p><span className="font-medium">ID:</span> {user.id}</p>
              <p><span className="font-medium">Name:</span> {user.name}</p>
              <p><span className="font-medium">Email:</span> {user.email || '—'}</p>
              <p><span className="font-medium">Role:</span> {user.role}</p>
              <p><span className="font-medium">Timezone:</span> {user.timezone}</p>
              <p><span className="font-medium">Provider:</span> {user.auth_provider}</p>
              <p><span className="font-medium">Company:</span> {user.company || '—'}</p>
            </div>
          </div>
        )} */}
      </div>
    </div>
  );
}
